import { renderTemplate } from "@/shared/services/template-renderer";
import { normalizeUserData } from "@/shared/services/user-normalizer";
import { isMobilePlatform, createInserter } from "@/shared/services/platform";
import { createLazySingleton } from "@/shared/services/lazy-singleton";
import { info, warn, error as logError, debug } from "@/shared/services/logger";
import {
  templateCache,
  templateKey,
  findMandatory,
  setTemplateSlot,
} from "@/features/signature/services/template-cache";
import { flushRoaming } from "@/shared/services/storage";
import { useToast } from "@/shared/composables/useToast";
import type { SignatureTemplate, UserTemplateData } from "@/shared/types";

const SOURCE = "sig-taskpane";

export { isMobilePlatform };

const getInserter = createLazySingleton(() => createInserter("taskpane"));

/**
 * Composable de gerenciamento de assinaturas no Outlook, com aplicação e salvamento de configurações.
 *
 * @returns Objeto com métodos de manipulação de assinatura.
 *
 * @example
 * ```typescript
 * const { applySignature, saveSettings } = useSignature();
 * applySignature(template, userData);
 * ```
 */
export function useSignature() {
  const toast = useToast();

  /**
   * Insere a assinatura renderizada no item atual via `setSignatureAsync` (mensagens)
   * ou `setAsync` (compromissos). Tenta HTML primeiro; se ocorrer erro 5000, repete com
   * texto puro para compatibilidade com corpos em plain text.
   *
   * @param template - Template de assinatura selecionado.
   * @param userData - Dados do usuário para preencher os placeholders do template.
   *
   * @example
   * ```typescript
   * const { applySignature } = useSignature();
   * applySignature(selectedTemplate, userData);
   * ```
   */
  function applySignature(
    template: SignatureTemplate,
    userData: UserTemplateData
  ): void {
    info(SOURCE, `applySignature: templateId=${template?.id}, templateName=${template?.name || "?"}, userEmail=${userData?.email || "?"}`);

    if (!template) {
      warn(SOURCE, "applySignature: template nulo — abortando.");
      showMessage("Template não encontrado.", "error");
      return;
    }

    if (!Office.context?.mailbox?.item) {
      warn(SOURCE, "applySignature: mailbox.item nulo — fora do contexto Outlook?");
      showMessage("Item do Outlook não disponível. Execute dentro do Outlook.", "error");
      return;
    }

    if (isMobilePlatform()) {
      warn(SOURCE, "applySignature: plataforma mobile — use copySignature.");
      showMessage("No mobile, use Copiar Assinatura para colar manualmente.", "info");
      return;
    }

    const rendered = renderTemplate(template.html, normalizeUserData(userData));
    debug(SOURCE, `applySignature: template renderizado — ${rendered.length} chars.`);

    if (!rendered || rendered.length < 5) {
      warn(SOURCE, `applySignature: renderização vazia (${rendered.length} chars).`);
      showMessage(`Renderização vazia (${rendered.length} chars). Verifique os dados.`, "error");
      return;
    }

    try {
      const item = Office.context.mailbox.item;

      if (typeof (item as any).getComposeTypeAsync !== "function") {
        warn(SOURCE, "applySignature: getComposeTypeAsync indisponível — fora do modo composição.");
        showMessage("Abra um email para responder ou escrever para inserir a assinatura.", "info");
        return;
      }

      info(SOURCE, `[TASKPANE] Delegando inserção (${rendered.length} chars).`);

      getInserter().insertSignature(rendered, item).then((result) => {
        if (result.success) {
          info(SOURCE, `[TASKPANE] Assinatura inserida — método=${result.method}.`);
          showMessage("Assinatura inserida!", "success");
        } else {
          logError(SOURCE, `[TASKPANE] Falha — método=${result.method}, code=${result.errorCode}, msg=${result.errorMessage}`);
          showMessage(`Erro (${result.errorCode}): ${result.errorMessage || "desconhecido"}`, "error");
        }
      });
    } catch (err: any) {
      logError(SOURCE, `[TASKPANE] Erro inesperado: ${err?.message || err}`);
      showMessage("Erro ao inserir assinatura.", "error");
    }
  }

  /**
   * Persiste as configurações de assinatura no roaming settings do Outlook.
   *
   * @param newMail - Identificador do template para novos e-mails.
   * @param reply - Identificador do template para respostas.
   * @param forward - Identificador do template para encaminhamentos.
   * @param overrideOlk - Se `true`, desabilita a assinatura nativa do Outlook.
   *
   * @example
   * ```typescript
   * const { saveSettings } = useSignature();
   * saveSettings("tpl-001", "tpl-002", "tpl-003", true);
   * ```
   */
  function saveSettings(newMail: string, reply: string, forward: string, overrideOlk: boolean): void {
    if (!Office.context.roamingSettings) {
      showMessage("Salvo (Simulação - Fora do Outlook)", "info");
      return;
    }

    const mandatory = findMandatory(templateCache.get() ?? []);
    const slots = mandatory
      ? { newMail: templateKey(mandatory.id), reply: templateKey(mandatory.id), forward: templateKey(mandatory.id) }
      : { newMail, reply, forward };

    setTemplateSlot("new_mail", slots.newMail);
    setTemplateSlot("reply", slots.reply);
    setTemplateSlot("forward", slots.forward);
    Office.context.roamingSettings.set("override_olk_signature", overrideOlk);

    void flushRoaming().then((result) => {
      if (result.ok) {
        showMessage("Configurações salvas!", "success");
        if (overrideOlk) {
          Office.context.mailbox.item?.disableClientSignatureAsync((res) => {
            if (res.status === Office.AsyncResultStatus.Succeeded) {
              info(SOURCE, "Assinatura nativa do Outlook desabilitada com sucesso.");
            } else {
              warn(SOURCE, `Falha ao desabilitar assinatura nativa: ${res.error?.message}`);
            }
          });
        }
      } else {
        showMessage("Erro ao salvar: " + (result.error?.message || "desconhecido"), "error");
      }
    });
  }

  /**
   * Exibe uma mensagem de feedback temporária na interface.
   *
   * @param text - Texto da mensagem a ser exibida.
   * @param type - Tipo da mensagem: `"success"`, `"error"` ou `"info"`.
   *
   * @example
   * ```typescript
   * showMessage("Operação concluída!", "success");
   * ```
   */
  function showMessage(
    text: string,
    type: "success" | "error" | "info"
  ): void {
    if (type === "success") toast.success(text);
    else if (type === "error") toast.error(text);
    else toast.info(text);
  }

  /**
   * Copia a assinatura renderizada para a área de transferência (clipboard),
   * com fallback para texto puro caso o navegador não suporte `ClipboardItem`.
   *
   * Útil principalmente no Outlook Mobile, onde `prependAsync`/`setSignatureAsync`
   * não estão disponíveis.
   *
   * @param template - Template de assinatura selecionado.
   * @param userData - Dados do usuário para preencher os placeholders do template.
   * @returns Promise que resolve quando a cópia for concluída.
   *
   * @example
   * ```typescript
   * const { copySignature } = useSignature();
   * await copySignature(template, userData);
   * ```
   */
  async function copySignature(
    template: SignatureTemplate,
    userData: UserTemplateData
  ): Promise<void> {
    const rendered = renderTemplate(template.html, normalizeUserData(userData));
    try {
      const htmlBlob = new Blob([rendered], { type: "text/html" });
      const textBlob = new Blob([rendered], { type: "text/plain" });
      const item = new ClipboardItem({
        "text/html": htmlBlob,
        "text/plain": textBlob,
      });
      await navigator.clipboard.write([item]);
      showMessage("Assinatura copiada!", "success");
    } catch {
      await navigator.clipboard.writeText(rendered);
      showMessage("Assinatura copiada (texto)!", "success");
    }
  }

  return {
    applySignature,
    copySignature,
    saveSettings,
  };
}
